"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMounted } from "@/shared/hooks/use-mounted"
import { getStepNumber } from "@/shared/routing/routes"
import getFRPromoBannerConfig from "@/shared/utils/banner"
import { SEND_MY_QUOTE_ENABLED } from "@/shared/utils/config"
import { isSendMyQuoteEligible } from "@/shared/utils/is-send-my-quote-eligible"
import { newSubscriptionFlowService } from "@/store/machine"
import { Banner, Skeleton, cn } from "@dalmatech/ui"
import { useActor } from "@xstate/react"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import { useEffect } from "react"
import { Trans } from "react-i18next"

import { Breadcrumb } from "../Breadcrumb/breadcrumb"
import { SendMyQuote } from "../SendMyQuote/send-my-quote"
import * as styles from "./navbar.styled"
import SelectLanguage from "./select-language"

const DynamicRightPartOfNav = dynamic(
  () => import("./right-part-of-nav").then((mod) => mod.RightPartOfNav),
  {
    ssr: false,
  }
)

export const Navbar = () => {
  const isMounted = useMounted()
  const { t } = useClientTranslation("global")
  const [state] = useActor(newSubscriptionFlowService)
  const pathname = usePathname()
  const promoCode = state.context.quote?.meta?.promo_code
  const rafCode = state.context.quote?.meta?.raf_code

  const bannerConfig = getFRPromoBannerConfig(promoCode, rafCode)
  useEffect(() => {
    if (bannerConfig) {
      const id = requestAnimationFrame(() => {
        return () => cancelAnimationFrame(id)
      })
    }
  }, [bannerConfig])
  const isSendMyQuoteVisible =
    SEND_MY_QUOTE_ENABLED &&
    isSendMyQuoteEligible(pathname) &&
    !!state.context.quote?.meta?.quote_id

  const completedStepsBarWidth = (getStepNumber(pathname) / 14) * 100

  return (
    <div className={styles.container}>
      {rafCode || promoCode ? (
        <Banner
          title={
            <Trans
              t={t}
              i18nKey={bannerConfig?.i18nKey || ""}
              values={bannerConfig?.values}
              components={{ strong: <strong /> }}
            />
          }
        />
      ) : null}
      <div className={styles.root}>
        <div className={styles.navbar}>
          <DynamicRightPartOfNav />
          {isMounted ? (
            <Breadcrumb className={styles.breadcrumb} />
          ) : (
            <Skeleton className={styles.skeleton} />
          )}
          <div className="flex gap-2">
            {isSendMyQuoteVisible ? <SendMyQuote /> : null}
            <SelectLanguage />
          </div>
        </div>
      </div>
      <div className={cn(styles.progressBar, "lg:hidden")}>
        <div
          className={styles.completedStepsBar}
          style={{ width: `${completedStepsBarWidth}%`, transition: "all 0.3s ease" }}
        />
        <div className={styles.activeStep}>
          <div className={styles.activeStepIndicator} />
        </div>
        <div className={styles.uncompletedStepsBar} />
      </div>
    </div>
  )
}
